import { isPhone, textParts } from '@/lib/links'
import { notionPage, type NotionPage } from '@/lib/notion'
import { NotionIcon } from './icons'
import './LinkedText.css'

/**
 * Plain text with line breaks kept and web addresses and phone numbers turned into links. A Notion page
 * shows as a chip with the page's name rather than its long address. With `links` off,
 * for a card's folded line that the card's own tap covers, nothing can be tapped, but
 * Notion pages keep their chips so the line still reads as their names.
 */
export function LinkedText({ text, links = true }: { text: string; links?: boolean }) {
  return (
    <>
      {textParts(text).map((part, i) => {
        if (!('href' in part)) return part.text
        const page = notionPage(part.href)
        if (page) return <NotionChip key={i} page={page} link={links} />
        if (!links) return part.text
        // a phone number rings from this page; a new tab would stay open and empty
        if (isPhone(part.href)) {
          return (
            <a key={i} href={part.href}>
              {part.text}
            </a>
          )
        }
        return (
          <a key={i} href={part.href} target="_blank" rel="noopener noreferrer">
            {part.text}
          </a>
        )
      })}
    </>
  )
}

function NotionChip({ page, link }: { page: NotionPage; link: boolean }) {
  const name = page.title ?? 'Notion page'
  const body = (
    <>
      <NotionIcon size={14} className="notion-chip__icon" />
      {name}
    </>
  )
  if (!link) return <span className="notion-chip">{body}</span>
  return (
    <a className="notion-chip notion-chip--link" href={page.url} target="_blank" rel="noopener noreferrer" title={page.url} aria-label={page.title ? `${page.title}, in Notion` : 'Notion page'}>
      {body}
    </a>
  )
}
